import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, injectIntl } from 'react-intl';
import capitalize from 'lodash/capitalize';
import Grid from '../../components/Grid';
import Item from '../../components/Item';
import Image from '../../components/Image';
import List from '../../components/List';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import Divider from '../../components/Divider';
import GenericModal from '../../components/GenericModal';

import FairAndVisitLinks from './fair-and-visit-links';
import UpcomingFair from './upcoming-fair';
import messages from './messages';
import heDefault from './heDefault.png';

import './styles.css';

class RepVisitsTravelPlanResults extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    institution: PropTypes.object.isRequired,
    displayNotification: PropTypes.func.isRequired,
    showSideBar: PropTypes.func.isRequired,
    isLastStateInstitution: PropTypes.bool.isRequired,
    viewer: PropTypes.shape({
      id: PropTypes.string.isRequired,
      repVisitsTravelPlan: PropTypes.arrayOf(PropTypes.object).isRequired,
      enabledFeatures: PropTypes.array,
    }).isRequired,
    removeTravelPlan: PropTypes.func.isRequired,
  };

  state = {
    openModal: false,
  };

  closeRemoveModal = () => {
    this.setState({ openModal: false });
  };

  showRemoveModal = () => {
    this.setState({ openModal: true });
  };

  removeTravelPlanAction = () => {
    const { displayNotification, institution: { id }, removeTravelPlan } = this.props;
    const onFailure = (error) => {
      displayNotification(false);
    };

    const onSuccess = () => {
      displayNotification(true);
      this.closeRemoveModal();
    };
    return removeTravelPlan({
      variables: {
        input: {
          highSchool: {
            id,
          },
        },
      },
    }).then(onSuccess).catch(onFailure);
  };

  render() {
    const { institution, showSideBar, isLastStateInstitution, viewer: { enabledFeatures } } = this.props;
    const dataCyInstitution = institution.name.split(' ').join('-');
    const { formatMessage } = this.props.intl;
    const isInactive = !institution.hasRepVisits && !institution.hasCollegeFairs;

    return (
      <Grid.Row styleName="result-container" data-cy={`repvisits-travel-plan-${dataCyInstitution}`}>
        <Grid.Column styleName="result-column" mobile={16} computer={12}>
          <Item.Group styleName="result-item">
            <Item>
              <Item styleName="result-avatar-container">
                <Image
                  styleName="result-avatar"
                  src={heDefault}
                  alt=""
                  centered
                />
                <div styleName="result-avatar-div">
                  <Button basic onClick={this.showRemoveModal}>
                    <Icon styleName="remove-btn-icon" name="trash alternate" />
                    <FormattedMessage {...messages.remove} />
                  </Button>
                </div>
              </Item>
              <Item.Content>
                <Item.Header styleName="result-institution-header-container">
                  <div styleName="result-institution-header-outer">
                    <h3 styleName="result-institution-header">{institution.name}</h3>
                  </div>
                </Item.Header>
                <Item.Meta styleName="result-institution-address">
                  {`${institution.address.field_hs_elna_address_line1 || ''}
                ${capitalize(institution.address.field_hs_elna_city)},
                ${capitalize(institution.address.field_hs_elna_state_latest)},
                ${capitalize(institution.address.field_hs_el_county_name)},
                ${institution.address.field_hs_eln_zip}`}
                </Item.Meta>
                <Item.Meta styleName="result-stats-container">
                  <List horizontal>
                    <List.Item>
                      <List.Content>
                        <List.Header styleName="result-stats-value">
                          {capitalize(institution.general.field_hs_elna_school_type)}
                        </List.Header>
                        <span styleName="result-stats-label">
                          <FormattedMessage {...messages.type} styleName="result-stats-label" />
                        </span>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Content>
                        <List.Header styleName="result-stats-value">
                          {institution.naviance.field_hs_na_percent_college}%
                        </List.Header>
                        <span styleName="result-stats-label">
                          <FormattedMessage {...messages.collegeGoingRate} styleName="result-stats-label" />
                        </span>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Content>
                        <List.Header styleName="result-stats-value">
                          {institution.enrollmentByGrade.field_hs_el_grade_12_students}
                        </List.Header>
                        <span styleName="result-stats-label">
                          <FormattedMessage {...messages.seniorClassSize} styleName="result-stats-label" />
                        </span>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <List.Content>
                        <List.Header styleName="result-stats-value">
                          {get(institution, 'repVisitsAccount.primaryContact.name', '')}
                        </List.Header>
                        <span styleName="result-stats-label">
                          <FormattedMessage {...messages.primaryPOC} />
                        </span>
                      </List.Content>
                    </List.Item>
                  </List>
                </Item.Meta>
                {institution.hasCollegeFairs && (
                <UpcomingFair
                  collegeFair={get(institution.collegeFairs, 'edges[0].node')}
                  highSchoolID={institution.id}
                />
                )}
              </Item.Content>
            </Item>
          </Item.Group>
        </Grid.Column>
        <Grid.Column mobile={16} computer={4}>
          {isInactive ? (
            <List styleName="result-actions-container">
              <List.Item styleName="result-action-container">
                <span styleName="result-action-italic">
                  <FormattedMessage {...messages.isntUsingRepVisits} />
                </span>
              </List.Item>
            </List>
          ): (
            <List
              styleName="result-actions-container"
              data-cy="repvisits-travel-plan-appointment-list"
            >
              <FairAndVisitLinks institution={institution} showSideBar={showSideBar} enabledFeatures={enabledFeatures} />
            </List>
          )}
        </Grid.Column>
        <Grid.Row width={16} centered styleName="result-divider-container">
          {!isLastStateInstitution && (
          <Divider styleName="result-divider" />
          )}
        </Grid.Row>
        <GenericModal
          open={this.state.openModal}
          title={formatMessage(messages.removeHeader)}
          message={formatMessage(messages.removeDescription, { institutionName: institution.name })}
          cancelText={formatMessage(messages.buttonCancel)}
          confirmText={formatMessage(messages.buttonRemove)}
          onCancel={this.closeRemoveModal}
          onConfirm={this.removeTravelPlanAction}
        />
      </Grid.Row>
    );
  }
}

export default injectIntl(RepVisitsTravelPlanResults);
